import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getSession,
  getPatient,
  getFlexResult,
  deleteSession,
  listSessions,
} from "../../store/db";
import type { Session, Patient } from "../../store/types";
import {
  FLEX_REGION_LABEL,
  type FlexResult,
  type FlexFinding,
  type FlexBand,
  type FlexRegion,
} from "../../flex/types";
import { PILOT } from "../../config";
import { TrendChart, type TrendSeries } from "../components/TrendChart";
import { buildFlexReportHtml, shareOrPrintReport, type FlexTrendPoint } from "../../report/report";

// Reuses ReviewScreen's existing .chip.b0/.b2/.b3 severity-chip CSS (see
// styles.css) instead of inventing new band classes — b1 (yellow-green) is
// skipped so the three Flex bands map onto clearly distinct chip colors.
function bandClass(band: FlexBand): string {
  switch (band) {
    case "GUT":
      return "b0";
    case "OK":
      return "b2";
    default:
      return "b3";
  }
}

/**
 * Self-vs-Self transparency value shown next to the Ampel band (see
 * docs/FLEX_WELLNESS_ABGRENZUNG.md §3a). Deliberately no clinical framing —
 * just the plain measured number, rounded to something a non-clinician can
 * read at a glance.
 */
function formatMagnitude(f: FlexFinding): string | null {
  const parts: string[] = [];
  if (f.unit === "deg") parts.push(`${Math.round(f.magnitude)}°`);
  if (f.reachPercent != null) parts.push(`Boden-Reichweite ≈ ${f.reachPercent}%`);
  return parts.length ? parts.join(" · ") : null;
}

function bandLabel(band: FlexBand): string {
  switch (band) {
    case "GUT":
      return "Gut";
    case "OK":
      return "Okay";
    default:
      return "Eingeschränkt";
  }
}

const REGION_ORDER: FlexRegion[] = [
  "hws",
  "schulter_brust",
  "rumpf",
  "huefte_hamstring",
  "knie_sprunggelenk",
];

function groupByRegion(findings: FlexFinding[]): Map<FlexRegion, FlexFinding[]> {
  const map = new Map<FlexRegion, FlexFinding[]>();
  for (const region of REGION_ORDER) map.set(region, []);
  for (const f of findings) {
    if (!map.has(f.region)) map.set(f.region, []);
    map.get(f.region)!.push(f);
  }
  return map;
}

function DeltaChip({ delta }: { delta: number | null }) {
  if (delta == null) return null;
  if (delta === 0) {
    return <span className="pill trend flat">± unverändert</span>;
  }
  // Negative Beweglichkeitsalter-Differenz = jünger = besser. Reuses
  // ReviewScreen's .pill.trend.up (green) / .down (orange) chip CSS.
  const improved = delta < 0;
  const arrow = improved ? "▼" : "▲";
  return (
    <span className={`pill trend ${improved ? "up" : "down"}`}>
      {arrow} {Math.abs(delta)} Jahr{Math.abs(delta) === 1 ? "" : "e"} vs. letzte Messung
    </span>
  );
}

export function FlexResultScreen() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [result, setResult] = useState<FlexResult | null>(null);
  const [history, setHistory] = useState<FlexTrendPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      const s = await getSession(sessionId);
      const r = await getFlexResult(sessionId);
      setSession(s ?? null);
      setResult(r ?? null);
      if (s) {
        const p = await getPatient(s.patientId);
        setPatient(p ?? null);

        // Verlauf des Beweglichkeitsalters über alle Flex-Screenings dieser
        // Person — nur der Composite-Wert, keine rohen Gelenkwinkel (siehe
        // docs/FLEX_WELLNESS_ABGRENZUNG.md).
        const allSessions = await listSessions(s.patientId);
        const flexSessions = allSessions.filter((x) => x.testType === "flex");
        const points: FlexTrendPoint[] = [];
        for (const fs of flexSessions) {
          const fr = await getFlexResult(fs.id);
          if (fr) points.push({ t: fr.recordedAt, sessionId: fs.id, flexAge: fr.flexAge.flexAge });
        }
        points.sort((a, b) => a.t - b.t);
        setHistory(points);
      }
      setLoading(false);
    })();
  }, [sessionId]);

  function onExportReport() {
    if (!result) return;
    void shareOrPrintReport(
      buildFlexReportHtml(patient, result, history),
      `${(patient?.code || "flex").replace(/\s+/g, "_")}_flex`,
    );
  }

  async function onDelete() {
    if (!sessionId) return;
    if (!confirm("Dieses Screening inkl. aller Aufnahmen löschen?")) return;
    await deleteSession(sessionId);
    nav("/flex");
  }

  if (loading) return <div className="card">Lädt…</div>;
  if (!session || !result) {
    return (
      <div className="card">
        <p>Screening nicht gefunden.</p>
      </div>
    );
  }

  const grouped = groupByRegion(result.findings);

  return (
    <>
      <div className="card hero">
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2>Beweglichkeitsalter: {result.flexAge.flexAge}</h2>
            <p className="muted">
              {patient?.code ?? "—"} ·{" "}
              {new Date(result.recordedAt).toLocaleDateString()}
              {result.flexAge.chronoAge != null && (
                <> · Lebensalter ca. {result.flexAge.chronoAge}</>
              )}
            </p>
            <DeltaChip delta={result.flexAge.comparedToPrevious} />
          </div>
          <button className="btn secondary" style={{ width: "auto", padding: "10px 14px" }} onClick={onExportReport}>
            Report (PDF)
          </button>
        </div>
      </div>

      {history.length > 1 && (
        <div className="card">
          <strong>Beweglichkeitsalter — Verlauf</strong>
          <div style={{ marginTop: 12 }}>
            <TrendChart
              series={[
                {
                  color: "#d97706",
                  pts: history.map((h) => ({ t: h.t, v: h.flexAge, id: h.sessionId })),
                } as TrendSeries,
              ]}
              min={Math.max(5, Math.min(...history.map((h) => h.flexAge)) - 3)}
              max={Math.min(99, Math.max(...history.map((h) => h.flexAge)) + 3)}
              onPick={(id) => nav(`/flex/review/${id}`)}
            />
          </div>
          <p className="muted" style={{ marginTop: 6 }}>Punkt antippen → Screening öffnen.</p>
        </div>
      )}

      {REGION_ORDER.filter((r) => (grouped.get(r) ?? []).length > 0).map((region) => (
        <div className="card" key={region}>
          <strong>{FLEX_REGION_LABEL[region]}</strong>
          <div style={{ marginTop: 8 }}>
            {(grouped.get(region) ?? []).map((f, i) => {
              const magStr = formatMagnitude(f);
              return (
                <div className="finding" key={`${f.key}-${f.side ?? ""}-${i}`}>
                  <span className="lbl">
                    {f.label}
                    {f.side ? ` (${f.side === "L" ? "links" : "rechts"})` : ""}
                    {magStr && <span className="muted"> · {magStr}</span>}
                  </span>
                  <span className={`chip ${bandClass(f.band)}`}>{bandLabel(f.band)}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="card">
        <button className="btn secondary" onClick={onDelete}>
          Screening löschen
        </button>
      </div>

      <p className="disclaimer">
        CADtomie Flex ist ein spielerisches Fitness-/Wellness-Tool — kein
        Medizinprodukt, keine klinische Diagnose und kein Ersatz für
        physiotherapeutische Untersuchung. Die Ampel-Einstufungen, das
        Beweglichkeitsalter und die angezeigten Grad-/Prozentwerte sind
        Self-vs-Self-Vergleichswerte auf Basis interner Heuristiken, keine
        medizinischen Normwerte und ohne Anspruch auf klinische
        Mess-Genauigkeit. Bei Beschwerden oder Unsicherheit bitte
        ärztlichen oder physiotherapeutischen Rat einholen.
        <br />
        <a href={`mailto:${PILOT.feedbackEmail}?subject=CADtomie%20Flex%20Feedback`}>
          Feedback senden
        </a>
      </p>
    </>
  );
}
